import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { useSystemContext } from "./hooks";
import axios from "axios";
import useConversations, {
  Conversation,
  updateLocalUser,
  USER_ID_LS_KEY,
} from "./ConversationLayer";
import { CHAT_INPUT_ID } from "src/widgets/copilot/components/constants";
import {
  CopilotChatWidgetController,
  useController,
} from "src/contexts/ControllerUtils";
import * as Sentry from "@sentry/react";
import { useMessageStore } from "./messages/useMessageStore";
import { useStreamingHandler } from "./messages/useStreamingHandler";

const CITATION_STYLE = "number";

const createNewQuery = (
  payload: RequestModel,
  createdAt = new Date().toISOString(),
) => {
  return {
    ...payload,
    id: uuidv4(),
    role: "user",
    created_at: createdAt,
  };
};

export const MessagesContext = createContext<MessagesContextType>({});

export interface MessagesContextType {
  messages?: Map<string, MessageMishmash>;
  isSending?: boolean;
  initializeQuery?: (payload: RequestModel) => void;
  rerun?: (run_url: string) => void;
  onEditQuery?: (
    messageId: string,
    payload: RequestModel,
    webUrl?: string,
  ) => void;
  // true when a host controller owns onEditQuery. Such a host re-runs the
  // edited turn on the server, so it needs the message's web_url; the local
  // handler below forks in the browser and does not. See ControllerUtils.
  isControllerEdit?: boolean;
  handleNewConversation?: () => void;
  cancelApiCall?: () => void;
  isReceiving?: boolean;
  conversations?: Conversation[] | null;
  setActiveConversation?: (conversation: Conversation) => Promise<void>;
  currentConversation?: Conversation | null;
  isMessagesLoading?: boolean;
  latestMessageIds?: Set<string>;
  preAttachedFileUsed?: boolean;
  setPreAttachedFileUsed?: (used: boolean) => void;
}

// --- Event Type Definitions ---

export interface ConversationStart {
  type: "conversation_start";
  conversation_id: string;
  user_id: string;
  user_message_id: string;
  bot_message_id: string;
  created_at: string;
}

export interface RunStart {
  type: "run_start";
  run_id: string;
  web_url: string;
  created_at: string;
  status_url: string;
}

export interface MessagePart {
  type: "message_part";
  status: string; // or a specific enum type if you have one
  detail: string;
  references?: SearchReference[];
  text?: string;
  audio?: string;
  video?: string;
  buttons?: ReplyButton[];
  documents?: string[];
  final_prompt?: any[];
}

export interface FinalResponse {
  type: "final_response";

  run_id?: string;
  web_url?: string;
  created_at?: string;
  status_url?: string;

  run_time_sec?: number;
  status?: string;
  detail?: string;

  /*
  These fields below are not supposed to be inlined but for some unknown reason they are.
  They must be in a nested `output` field to match the backend API

  output?: CopilotOutput | null;
  */

  final_prompt: string | unknown[];
  output_text: string[];
  output_audio: string[];
  output_video: string[];

  // intermediate text
  raw_input_text?: string | null;
  raw_tts_text?: string[] | null;
  raw_output_text?: string[] | null;

  // doc search
  references?: SearchReference[] | null;
  final_search_query?: string | null;
  final_keyword_query?: string | string[] | null;

  // output_documents?: string[] | null;
  // reply_buttons?: ReplyButton[] | null;

  finish_reason?: string[] | null;
}

export interface StreamError {
  type: "error";
  detail: string;
}

// --- Supporting Types ---

export interface ReplyButton {
  id: string;
  title: string;
  action?: string;
  payload?: Record<string, unknown>;
}

export interface SearchReference {
  url: string;
  title: string;
  snippet: string;
  score: number;
}

export interface OpenAPIMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  input_images?: string[];
  input_documents?: string[];
  input_audio?: Blob | string;
}

export interface RequestModel {
  conversation_id?: string;
  user_id?: string;
  button_pressed?: {
    button_id: string;
    context_msg_id: string;
    button_title?: string | null;
  };
  input_location?: {
    latitude?: number;
    longitude?: number;
  };
  input_prompt?: string;
  input_audio?: Blob | string;
  input_images?: string[];
  input_documents?: string[];
  citation_style?: string;
  messages?: OpenAPIMessage[];
}

// This is absolutely disgusting, but it works
export type MessageMishmash = {
  id: string;
  role: "user" | "assistant";
  // the run this message belongs to. The server stamps it on both halves of a
  // turn, so a user message can be pointed back at the run that answered it.
  web_url?: string;
} & (
  | RequestModel
  | ConversationStart
  | RunStart
  | MessagePart
  | FinalResponse
  | StreamError
);

const MessagesContextProvider = ({
  controller,
  shadowRoot,
  children,
}: {
  controller?: CopilotChatWidgetController;
  shadowRoot: ShadowRoot | undefined;
  children: React.ReactNode;
}) => {
  const currentUserId = localStorage.getItem(USER_ID_LS_KEY) || "";
  const { config, layoutController } = useSystemContext();
  const { conversations, handleAddConversation, handleDeleteConversation } =
    useConversations(currentUserId, config?.integration_id as string);

  const {
    messages,
    setMessages,
    latestMessageIds,
    setLatestMessageIds,
    preAttachedFileUsed,
    setPreAttachedFileUsed,
    addResponse,
    preLoadData,
    purgeMessages: purgeMessagesStore,
  } = useMessageStore();
  const [isSending, setIsSendingMessage] = useState(false);
  const [isReceiving, setIsReceiving] = useState(false);
  const [isMessagesLoading, setMessagesLoading] = useState(true);
  const [isSharedConversation, setIsSharedConversation] = useState(false);

  const isServerMode = !!config?.controller?.fetchConversations;

  const apiSource = useRef(axios.CancelToken.source());
  const currentConversation = useRef<Conversation | null>(null);
  // Id of the conversation an edit is replacing. Editing a message forks a
  // fresh backend conversation (new conversation_id); once that fork's first
  // response is saved we delete this stale entry so the edit replaces it in the
  // saved list rather than adding a duplicate. Set in onEditQuery, consumed on
  // the next finalize, and cleared if the user switches conversations first
  // (purgeMessages / setActiveConversation / controller.setConversationData).
  const conversationIdToReplace = useRef<string | null>(null);

  const handleConversationFinalized = async (
    conversation: Conversation | null,
  ) => {
    if (isServerMode || !conversation) {
      // No local conversation was persisted, so any armed edit replacement no
      // longer has a matching finalize to consume it — clear it.
      conversationIdToReplace.current = null;
      return;
    }
    await handleAddConversation(conversation);
    // Drop the pre-edit conversation now that its replacement is persisted.
    const staleId = conversationIdToReplace.current;
    conversationIdToReplace.current = null;
    if (staleId && staleId !== conversation.id) {
      await handleDeleteConversation(staleId);
    }
  };

  const updateCurrentConversation = (conversation: Conversation) => {
    currentConversation.current = {
      ...currentConversation.current,
      ...conversation,
    };
  };

  const initializeQuery = (
    payload: RequestModel,
    opts?: { startNewConversation?: boolean },
  ) => {
    if (!payload || isSending || isReceiving) return;
    // Clear any previously received message IDs when starting a new query
    setLatestMessageIds(new Set());

    // calls the server and updates the state with user message
    const conversationId =
      opts?.startNewConversation || isSharedConversation
        ? undefined
        : currentConversation.current?.id;
    setIsSendingMessage(true);
    if (
      !payload.messages &&
      !conversationId &&
      currentConversation.current?.messages
    ) {
      // make messages array in payload from messages in currentConversation and add
      payload.messages = currentConversation.current?.messages?.map(
        (message) => ({
          id: message.id,
          role: message.role,
          content:
            message.role === "user"
              ? message.input_prompt || ""
              : message.raw_output_text?.[0] || "",
        }),
      );
    }
    setIsSharedConversation(false); //reset shared conversation flag
    // Stamp created_at once so the sent payload and the local message (and thus
    // the persisted conversation timestamp) agree on the same time.
    const createdAt = new Date().toISOString();
    const payloadWithCreatedAt = { ...payload, created_at: createdAt };
    sendPayload(
      {
        ...payloadWithCreatedAt,
        conversation_id: conversationId,
        citation_style: CITATION_STYLE,
        user_id: currentUserId,
      },
      { onFinally: () => setIsSendingMessage(false) },
    ).catch((e) => {
      // An aborted/failed fork must not leave a pending edit replacement armed,
      // or a later finalize could delete the wrong (pre-edit) conversation.
      conversationIdToReplace.current = null;
      // report error to Sentry
      Sentry.captureException(e);
    });
    const newQuery = createNewQuery(payloadWithCreatedAt, createdAt);
    addResponse(newQuery);
  };

  const onEditQuery = (messageId: string, payload: RequestModel) => {
    if (isSending || isReceiving) return;
    const entries = Array.from(messages.entries());
    const idx = entries.findIndex(([id]) => id === messageId);
    if (idx < 0) return;
    // keep everything before the edited message; drop it and all that follow
    const kept = entries.slice(0, idx);
    setMessages(new Map(kept));
    // build the truncated history so the bot regenerates ignoring removed turns
    const history: OpenAPIMessage[] = kept.map(([id, message]) => {
      if (message.role === "user") {
        const userMessage = message as RequestModel;
        return {
          id,
          role: "user",
          content: userMessage.input_prompt || "",
          // keep prior-turn attachments so the fork regenerates with full context
          input_images: userMessage.input_images,
          input_documents: userMessage.input_documents,
          input_audio: userMessage.input_audio,
        };
      }
      return {
        id,
        role: "assistant",
        content:
          (message as FinalResponse).raw_output_text?.[0] ||
          (message as FinalResponse).output_text?.[0] ||
          "",
      };
    });
    // Fork a fresh backend conversation, then replace the conversation being
    // edited: its saved entry is deleted once the fork's first response lands.
    // Null when editing an unsaved conversation (nothing to replace).
    conversationIdToReplace.current = currentConversation.current?.id ?? null;
    initializeQuery(
      { ...payload, messages: history },
      { startNewConversation: true },
    );
  };

  const { sendPayload } = useStreamingHandler({
    config,
    onConversationFinalized: handleConversationFinalized,
    updateCurrentConversation,
    setIsReceiving,
    setIsSendingMessage,
    setLatestMessageIds,
    setMessages,
    apiSource,
    currentUserId,
    preAttachedFileUsed,
    updateLocalUser,
  });

  const handleNewConversation = () => {
    if (isReceiving || isSending) {
      cancelApiCall();
    }
    if (layoutController?.isNarrowWidth && layoutController?.isSidebarOpen)
      layoutController?.toggleSidebar();
    setIsReceiving(false);
    setIsSendingMessage(false);
    setPreAttachedFileUsed(false); // Reset for new conversation
    purgeMessages();
    const ele = shadowRoot?.getElementById(CHAT_INPUT_ID);
    ele?.focus();
  };

  const purgeMessages = () => {
    purgeMessagesStore();
    currentConversation.current = {};
    // Fresh chat: pending edit-replacement no longer applies.
    conversationIdToReplace.current = null;
  };

  const cancelApiCall = useCallback(() => {
    // Canceling an in-flight (possibly edit) request must not leave a pending
    // edit replacement armed for a later, unrelated finalize.
    conversationIdToReplace.current = null;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (window?.GooeyEventSource) GooeyEventSource.close();
    else apiSource?.current.cancel("Operation canceled by the user.");

    if (!isReceiving && !isSending) {
      apiSource.current = axios.CancelToken.source(); // set new cancel token for next api call
    }
    // delete last message from the state
    const newMessages = new Map(messages);
    const idsArray = Array.from(messages.keys());
    // check if state is loading then remove the last one
    if (isSending) {
      newMessages.delete(idsArray.pop());
      setMessages(newMessages);
    }

    if (isReceiving) {
      newMessages.delete(idsArray.pop()); // delete server message
      newMessages.delete(idsArray.pop()); // delete user message
      setMessages(newMessages);
    }

    updateCurrentConversation({
      messages: Array.from(newMessages.values()),
    });

    apiSource.current = axios.CancelToken.source(); // set new cancel token for next api call
    setIsReceiving(false);
    setIsSendingMessage(false);
  }, [isReceiving, isSending, messages]);

  const setActiveConversation = useCallback(
    async (conversation: Conversation) => {
      if (isSending || isReceiving) cancelApiCall();

      if (!conversation || currentConversation.current?.id === conversation.id)
        return setMessagesLoading(false);
      setMessagesLoading(true);
      let messages = [];
      if (!conversation.getMessages && conversation.messages) {
        messages = conversation.messages;
      } else if (conversation.getMessages) {
        messages = await conversation.getMessages();
      }
      if (conversation.id && controller?.onConversationChange)
        controller?.onConversationChange?.(conversation.id);
      preLoadData(messages);
      updateCurrentConversation(conversation);
      // Switched to a real saved conversation: cancel any pending replacement.
      conversationIdToReplace.current = null;
      setMessagesLoading(false);
    },
    [cancelApiCall, isReceiving, isSending, controller],
  );

  useEffect(() => {
    let loadLatestConversation = !layoutController?.showNewConversationButton;

    if (loadLatestConversation && conversations?.length && !messages.size)
      // Load the latest conversation from DB - initial load when multiple conversations are disabled
      setActiveConversation(conversations[0]);
    else if (config?.conversationData) {
      if (!conversations) return;
      // shared conversation preloading logic
      const existingConversation = conversations.find(
        (conversation) => conversation.id === config?.conversationData?.id,
      );

      // checks conversation.id is already in the conversations DB, set it as the active conversation
      if (existingConversation && !config?.conversationData?.last_message_id) {
        setActiveConversation(existingConversation);
        config.conversationData = null;
      } else {
        // new conversation and user
        setActiveConversation(config?.conversationData);
        setIsSharedConversation(true); // for new conversation and user
        config.conversationData = null;
        setMessagesLoading(false);
      }
    } else setMessagesLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations]);

  if (controller) {
    controller.setConversationData = async (conversation: Conversation) => {
      if (isSending || isReceiving) return;
      currentConversation.current = conversation;
      // Host switched the conversation: cancel any pending replacement.
      conversationIdToReplace.current = null;
      if (
        conversation.messages &&
        messagesChanged(Array.from(messages.values()), conversation.messages)
      ) {
        preLoadData(conversation.messages);
      }
    };
  }

  let controllerContext = useController({
    controller,
    apiUrl: config!.apiUrl!,
    isSending,
    isReceiving,
  });

  let context: MessagesContextType = {
    messages,
    isSending,
    initializeQuery,
    onEditQuery,
    handleNewConversation,
    cancelApiCall,
    isReceiving,
    conversations,
    setActiveConversation,
    currentConversation: currentConversation.current || null,
    isMessagesLoading,
    latestMessageIds,
    preAttachedFileUsed,
    setPreAttachedFileUsed,
    ...controllerContext,
  };

  return (
    <MessagesContext.Provider value={context}>
      {children}
    </MessagesContext.Provider>
  );
};

export default MessagesContextProvider;

function messagesChanged(array1: any[], array2: any[]): boolean {
  if (array1.length !== array2.length) {
    return true;
  }
  for (let i = 0; i < array1.length; i++) {
    // compare the content of the messages, ignore the id
    if (
      JSON.stringify(array1[i], removeMsgId) !==
      JSON.stringify(array2[i], removeMsgId)
    ) {
      return true;
    }
  }
  return false;
}

function removeMsgId(key: string, value: any) {
  if (key === "id") {
    return undefined;
  } else {
    return value;
  }
}
